import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { SupportContact } from '@/shared/platform/SupportContact';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { IconAlertCircle, IconLock, IconLogin2 } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { AlternativeLogins } from '@/features/auth/AlternativeLogins';
import { isPortalSessionSync } from '@/shared/lib/auth-state';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { mw, MW_AUTH_CARD_SHADOW } from '@/shared/mobile/tokens';

import { leggiChiave, writeKey, removeKey } from '@/shared/lib/storage';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { userFacingMessage } from '@/shared/api/errorMessage';
import { safeInternalHref } from '@/shared/lib/safeRedirect';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(1, 'Password obbligatoria'),
});

type LoginForm = z.infer<typeof loginSchema>;

// Cambio password obbligatorio al primo accesso (login → 409). Il BE impone min 6 / max 100.
const changePwdSchema = z
  .object({
    newPassword: z.string().min(6, 'Minimo 6 caratteri').max(100, 'Massimo 100 caratteri'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Le password non coincidono',
    path: ['confirmPassword'],
  });

type ChangePwdForm = z.infer<typeof changePwdSchema>;

interface LoginSearch {
  redirect?: string;
}

/**
 * Dove mandare chi ha appena fatto login.
 *
 * Un utente-cliente **non** può entrare nella shell interna: il muro del BE nega tutto ciò
 * che sta fuori da `/api/client-portal/*`, quindi `AppShell` prenderebbe 403 su
 * configurazione tenant, dipendente collegato, notifiche e contatori — una schermata di
 * avvisi rossi invece del suo portale.
 *
 * Il marker si legge dal mirror (e dalla sua copia persistita), non dal `decoded` del
 * componente: la closure ha catturato lo stato di **prima** del login, e a quel punto
 * sarebbe sempre vuoto. Se anche qui arrivasse tardi, la guardia di `_app` rimbalza
 * comunque: questo redirect è la via veloce, non l'unica.
 *
 * Il `redirect` richiesto vale solo per gli interni: se un utente-cliente arriva con un
 * link profondo a una pagina interna, mandarcelo sarebbe mandarlo su un 403.
 */
function postLoginDestination(redirect?: string): string {
  if (isPortalSessionSync()) return '/portal';
  return safeInternalHref(redirect) ?? '/';
}

export const Route = createFileRoute('/_auth/login')({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  component: LoginPage,
});

const REMEMBER_KEY = STORAGE_KEYS.rememberEmail;

function LoginPage() {
  const { login, loginWithEnvelope, changePasswordAndLogin } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remember, setRemember] = useState(false);
  const [attempts, setAttempts] = useState(0);
  // Quando valorizzato, il login ha risposto 409 → mostra il form di cambio password obbligatorio.
  const [mustChange, setMustChange] = useState<{ email: string; oldPassword: string } | null>(null);

  const form = useForm<LoginForm>({
    mode: 'controlled',
    initialValues: { email: '', password: '' },
    validate: schemaResolver(loginSchema, { sync: true }),
  });

  const changeForm = useForm<ChangePwdForm>({
    mode: 'controlled',
    initialValues: { newPassword: '', confirmPassword: '' },
    validate: schemaResolver(changePwdSchema, { sync: true }),
  });

  useEffect(() => {
    try {
      const remembered = leggiChiave(REMEMBER_KEY);
      if (remembered) {
        form.setFieldValue('email', remembered);
        setRemember(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = form.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      await login(values.email, values.password);
      try {
        if (remember) writeKey(REMEMBER_KEY, values.email);
        else removeKey(REMEMBER_KEY);
      } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 0));
      void navigate({ href: postLoginDestination(search.redirect), replace: true });
    } catch (err) {
      // 409 = primo accesso / password scaduta → cambio password obbligatorio.
      if (err instanceof JsonApiHttpError && err.status === 409) {
        setMustChange({ email: values.email, oldPassword: values.password });
        setError(null);
        changeForm.reset();
        return;
      }
      // 404 = utente inesistente (il BE risponde "User not found" in inglese).
      const msg = err instanceof JsonApiHttpError
        ? (err.status === 404
            ? 'Nessun account trovato con questa email.'
            : err.getUserMessage())
        : userFacingMessage(err, 'Errore di accesso');
      setError(msg);
      setAttempts((n) => n + 1);
    } finally {
      setBusy(false);
    }
  });

  const onChangeSubmit = changeForm.onSubmit(async (vals) => {
    if (!mustChange) return;
    setBusy(true);
    setError(null);
    try {
      await changePasswordAndLogin(mustChange.email, mustChange.oldPassword, vals.newPassword);
      try {
        if (remember) writeKey(REMEMBER_KEY, mustChange.email);
      } catch { /* ignore */ }
      // Yield per far propagare lo stato auth (come nel login normale), poi redirect.
      await new Promise((r) => setTimeout(r, 0));
      void navigate({ href: postLoginDestination(search.redirect), replace: true });
    } catch (err) {
      if (err instanceof JsonApiHttpError && err.status === 403) {
        // Il 409 del login scatta PRIMA della verifica password: se quella digitata era
        // sbagliata, il cambio fallisce qui con 403 (old password mismatch). Torna al login.
        setMustChange(null);
        setError('La password inserita al login non è corretta. Riprova.');
        return;
      }
      if (err instanceof JsonApiHttpError && err.status === 409) {
        // Password riusata di recente (history check del BE).
        setError('Questa password è stata usata di recente: scegline una diversa.');
        return;
      }
      const msg = userFacingMessage(err, 'Errore nel cambio password');
      setError(msg);
    } finally {
      setBusy(false);
    }
  });

  return (
    /*
     * Spaziature piu' strette sul telefono, invariate da `sm` in su.
     *
     * Sul mobile la card pesava 621 px, di cui **165 di sole spaziature**: i 34 px di padding su
     * quattro lati, il `gap="xl"` verso il piede e i `gap` interni. Con il marchio e la testata
     * sopra, la pagina di login sforava di 93 px su uno schermo da 812 e molto di piu' sotto —
     * cioe' si doveva scorrere per arrivare al pulsante Entra.
     *
     * Nessun contenuto e' stato tolto: cambiano solo i vuoti, e solo dove servono a qualcosa di
     * meno che vedere la form intera. Le misure stanno nel CSS di `_auth.tsx` e non nelle prop
     * perche' in Mantine 7 `gap` e `p` non accettano valori responsive: sono `MantineSpacing`,
     * non `StyleProp<MantineSpacing>`.
     */
    <Stack className="mw-auth-colonna">
      <Paper
        className="mw-auth-card"
        radius={22}
        style={{ border: `1px solid ${mw.line}`, boxShadow: MW_AUTH_CARD_SHADOW }}
      >
        <Stack className="mw-auth-campi">
          {mustChange ? (
            <>
              <div>
                <Title order={3}>Imposta una nuova password</Title>
                <Text c="dimmed" size="sm">
                  Al primo accesso è necessario scegliere una nuova password per{' '}
                  <strong>{mustChange.email}</strong>.
                </Text>
              </div>

              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm" fw={500}>{error}</Text>
                </Alert>
              )}

              <form onSubmit={onChangeSubmit}>
                <Stack>
                  <PasswordInput
                    label="Nuova password"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    leftSection={<IconLock size={16} />}
                    {...changeForm.getInputProps('newPassword')}
                  />
                  <PasswordInput
                    label="Conferma password"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    leftSection={<IconLock size={16} />}
                    {...changeForm.getInputProps('confirmPassword')}
                  />
                  <Button type="submit" loading={busy} fullWidth size="md">
                    Cambia password ed entra
                  </Button>
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    c="dimmed"
                    ta="center"
                    onClick={() => { setMustChange(null); setError(null); }}
                  >
                    Torna al login
                  </Anchor>
                </Stack>
              </form>
            </>
          ) : (
            <>
              <Group gap="sm" wrap="nowrap" align="center">
                <ThemeIcon visibleFrom="md" size={44} radius="md" variant="filled" color="brand">
                  <IconLogin2 size={24} />
                </ThemeIcon>
                <div>
                  <Title order={3}>Bentornato</Title>
                  <Text c="dimmed" size="sm">Accedi al tuo account per continuare.</Text>
                </div>
              </Group>

              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm" fw={500}>{error}</Text>
                  {attempts >= 3 && (
                    <Text size="xs" mt={4} c="dimmed">
                      Tentativo {attempts}. Se hai dimenticato la password,{' '}
                      <Anchor component={Link} to="/password-reset" size="xs" c={mw.accentText} fw={500}>recuperala qui</Anchor>.
                    </Text>
                  )}
                </Alert>
              )}

              <form onSubmit={onSubmit}>
                <Stack>
                  <TextInput
                    label="Email"
                    placeholder="tu@azienda.it"
                    required
                    autoComplete="username"
                    {...form.getInputProps('email')}
                  />
                  <PasswordInput
                    label="Password"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    leftSection={<IconLock size={16} />}
                    {...form.getInputProps('password')}
                  />
                  <Group justify="space-between">
                    <Checkbox
                      size="sm"
                      label="Ricordami"
                      checked={remember}
                      onChange={(e) => setRemember(e.currentTarget.checked)}
                    />
                    {/* Recupero con codice a 6 cifre. `/recovery-password` (flusso a link) resta
                        raggiungibile e funzionante, ma non è più la porta d'ingresso dal login. */}
                    <Anchor component={Link} to="/password-reset" size="sm" c={mw.accentText} fw={500}>
                      Password dimenticata?
                    </Anchor>
                  </Group>
                  <Button type="submit" loading={busy} fullWidth size="md">
                    Entra
                  </Button>
                </Stack>
              </form>

              <AlternativeLogins
                onSuccess={(envelope) => {
                  setError(null);
                  loginWithEnvelope(envelope);
                  // Stesso pattern del login password: yield per propagare lo stato auth.
                  void Promise.resolve().then(async () => {
                    await new Promise((r) => setTimeout(r, 0));
                    void navigate({ href: postLoginDestination(search.redirect), replace: true });
                  });
                }}
                onError={(msg) => setError(msg)}
              />

              <Group justify="center">
                <Text size="sm" c="dimmed">Non hai un account?</Text>
                <Anchor component={Link} to="/register" size="sm" fw={600} c={mw.accentText}>
                  Registrati
                </Anchor>
              </Group>
            </>
          )}
        </Stack>
      </Paper>

      <Box ta="center">
        <Text size="xs" c="dimmed">
          Hai bisogno di aiuto?{' '}
          <SupportContact withStatus />
        </Text>
      </Box>
    </Stack>
  );
}
